import React, { useState, useEffect } from "react";
import AdvertiseHero from "../components/AdvertiseHero";
import AdvertiseSection from "../components/AdvertiseSection";
import { api } from "../config/api";
import "../components/AdvertiseWithUs.css";
import AdvertiseDownload from "../components/AdvertiseDownload";
import AdvertiseGetDetails from "../components/AdvertiseGetDetails";

const PAGE_KEY = "advertise-with-us";
const SHOW_RATE_CARD = false;

const AdvertiseWithUs = () => {
  const [pageData, setPageData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchPageData = async () => {
      try {
        const response = await api.getPageConfigByKey(PAGE_KEY);
        console.log("AdvertiseWithUs API response:", response);
        if (response && response.id) {
          setPageData(response);
          console.log("AdvertiseWithUs pageData set:", response);
        } else {
          setError("No data received from API");
          setPageData(null);
        }
      } catch (error) {
        console.error("Failed to fetch page config:", error);
        setError(error.message || "Failed to load page data");
        setPageData(null);
      } finally {
        setLoading(false);
      }
    };

    fetchPageData();
  }, []);

  // Transform API data to match the expected format
  const transformApiData = (apiData) => {
    if (!apiData) return null;

    console.log("Transforming API data:", apiData);

    const allSections = apiData.sections || [];

    // Extract tabs (sections with section_type "tab")
    const tabSections = allSections.filter(
      (s) => s.data?.section_type === "tab" && s.is_active,
    );
    const tabs = tabSections
      .sort((a, b) => a.display_order - b.display_order)
      .map((s) => ({
        id: s.id,
        slug: s.slug,
        tab_group: s.data?.tab_group || s.slug,
        title: s.title,
        display_order: s.display_order,
      }));

    // Extract content sections (not tabs, rate_card, download, advertising_details, ui_labels)
    const sections = allSections.filter(
      (s) =>
        s.is_active &&
        s.data?.section_type !== "rate_card" &&
        s.data?.section_type !== "download" &&
        s.data?.section_type !== "advertising_details" &&
        s.data?.section_type !== "tab" &&
        s.data?.section_type !== "ui_labels",
    );

    const rateCardSection = allSections.find(
      (s) => s.data?.section_type === "rate_card",
    );

    const downloadSection = allSections.find(
      (s) => s.data?.section_type === "download",
    );

    const advertisingDetailsSection = allSections.find(
      (s) => s.data?.section_type === "advertising_details",
    );

    console.log("Found tabs:", tabs.length);
    console.log("Found sections:", sections.length);
    console.log("Found rate card:", !!rateCardSection);
    console.log("Found download section:", !!downloadSection);
    console.log(
      "Found advertising details:",
      !!advertisingDetailsSection,
    );

    // Transform sections to match the expected format
    const transformedSections = sections.map((section) => {
      const tabGroup = section.data?.tab_group || "onscreen";
      const slug = section.slug;

      // Normalize images: support both string[] and {url, caption}[]
      const rawImages = section.data?.images || [];
      const normalizedImages = rawImages.map((img) => {
        if (typeof img === "string") {
          return { url: img, caption: "" };
        }
        return { url: img.url || "", caption: img.caption || "" };
      });

      return {
        id: slug,
        title: section.title,
        data: {
          tab_group: tabGroup,
          content: section.data?.content || "",
          content_heading: section.data?.content_heading || "",
          list_heading: section.data?.list_heading || "",
          whatsapp_number: section.data?.whatsapp_number || "",
          whatsapp_prefilled_text: section.data?.whatsapp_prefilled_text || "",
        },
        content: section.data?.content || "",
        content_heading: section.data?.content_heading || "",
        list_heading: section.data?.list_heading || "",
        images: normalizedImages,
        cards: section.data?.cards || [],
        list: section.data?.list_items || [],
        whatsapp_number: section.data?.whatsapp_number || "",
        whatsapp_prefilled_text: section.data?.whatsapp_prefilled_text || "",
      };
    });

    return {
      hero: {
        title: apiData.title || "Advertise With Us",
        subtitle: apiData.subtitle || "",
        description:
          apiData.description ||
          "Reach thousands of movie lovers by advertising with us.",
      },
      tabs: tabs,
      sections: transformedSections,
      rateCard: {
        rows: rateCardSection?.data?.rate_rows || [],
      },
      downloadSection: downloadSection
        ? {
            title:
              downloadSection.data?.download_title ||
              "Download Detailed Document",
            buttonText: downloadSection.data?.button_text || "Download",
            documentUrl: downloadSection.data?.document_url || "",
          }
        : null,
      advertisingDetails: advertisingDetailsSection
        ? {
            is_active: advertisingDetailsSection.is_active,
            data: advertisingDetailsSection.data || {},
          }
        : null,
    };
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center p-5">
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  if (error || !pageData) {
    return (
      <div className="container py-5">
        <div className="alert alert-warning" role="alert">
          <h4 className="alert-heading">Unable to load page content</h4>
          <p className="mb-0">
            {error ||
              "No data available. Please configure the page in the admin panel."}
          </p>
        </div>
      </div>
    );
  }

  const data = transformApiData(pageData);
  const { hero, tabs, sections, rateCard, downloadSection, advertisingDetails } = data;

  return (
    <div className="advertise-page">
      <AdvertiseHero hero={hero} />
      <AdvertiseSection tabs={tabs} sections={sections} />
      {advertisingDetails && advertisingDetails.is_active && (
        <AdvertiseGetDetails details={advertisingDetails} />
      )}

      {/* Rate card (simple table) */}
      {SHOW_RATE_CARD && rateCard && rateCard.rows && rateCard.rows.length > 0 && (
        <div className="rate-card">
          <div className="container">
            <div className="row">
              <div className="inner-banner">
                <h2 className="text-center">Rate Card</h2>
                <div className="table-responsive">
                  <table className="table th-table">
                    <thead>
                      <tr>
                        <th>Description</th>
                        <th>Rates (₹)</th>
                        <th>Special Rates (₹)</th>
                        <th>GST</th>
                        <th>Duration</th>
                        <th>Unit</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rateCard.rows.map((r, idx) => (
                        <tr key={idx}>
                          <td>{r.description}</td>
                          <td>{r.rate}</td>
                          <td>{r.special}</td>
                          <td>{r.gst}</td>
                          <td>{r.duration}</td>
                          <td>{r.unit}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
        {downloadSection && (
        <AdvertiseDownload downloadSection={downloadSection} />
      )}
    </div>
  );
};

export default AdvertiseWithUs;
